import { useMutation } from '@tanstack/react-query';
import { Flag, Mail, Pencil, Phone } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EditContactDialog } from './contact-dialog';
import type { ContactDetails } from './types';
import { useCan } from '~/hooks/use-can';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { useTRPC } from '~/lib/trpc';
import { cn } from '~/lib/utils';

/**
 * What we know about this person, and nothing you can change here.
 *
 * Every edit is behind the pencil, so this panel has one job and the rules that
 * govern a channel — who owns it, whether removing it would strand anything —
 * live in one place rather than being half-expressed by the affordances beside
 * each badge. The flag stays: it is triage state, clicked constantly while
 * reading the list, not an edit.
 */

export function ContactInfoPanel({
  contact,
}: {
  contact: NonNullable<ContactDetails>;
}) {
  const canWork = useCan('reception');
  const { t } = useTranslation('tasks');
  const trpc = useTRPC();
  const { mutateAsync: setImportant } = useMutation(
    trpc.contacts.directory.setImportant.mutationOptions(),
  );
  const [isSavingImportant, setIsSavingImportant] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const handleToggleImportant = async () => {
    setIsSavingImportant(true);

    try {
      await setImportant({
        contactId: contact.id,
        isImportant: !contact.isImportant,
      });
    } finally {
      setIsSavingImportant(false);
    }
  };
  const importantLabel = contact.isImportant
    ? t('Unmark as important')
    : t('Mark as important');

  return (
    <aside className="flex h-full min-w-[384px] flex-col border-l border-border bg-background">
      <div className="flex items-start gap-4 border-b border-border p-5">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex h-10 items-center gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                shape="pill"
                disabled={!canWork || isSavingImportant}
                onClick={() => void handleToggleImportant()}
                aria-label={importantLabel}
                className="hover:bg-transparent"
              >
                <Flag
                  className={cn(
                    'size-4',
                    contact.isImportant
                      ? 'fill-destructive text-destructive'
                      : 'text-muted-foreground',
                  )}
                  strokeWidth={2.5}
                />
              </Button>
              <h2 className="min-w-0 flex-1 truncate text-lg font-semibold text-foreground">
                {contact.displayName}
              </h2>
            </div>
            <Button
              type="button"
              variant="muted"
              size="icon-xs"
              shape="pill"
              disabled={!canWork}
              onClick={() => setIsEditing(true)}
              aria-label={t('Edit contact')}
            >
              <Pencil className="size-4" strokeWidth={2.5} />
            </Button>
          </div>

          {/* The bordered one is the default: the one a receptionist reaches
              for, and — because the contact search indexes read only these two
              fields — the only one they can find this person by. */}
          <div className="flex flex-col items-start gap-1">
            {contact.phoneNumbers.map((number) => (
              <ContactBadge
                key={number}
                icon={Phone}
                value={number}
                isDefault={number === contact.phone}
              />
            ))}
            {contact.emails.map((email) => (
              <ContactBadge
                key={email}
                icon={Mail}
                value={email}
                isDefault={email === contact.email}
              />
            ))}
          </div>

          {contact.tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              {/* A tag is provenance — `Formularz` says these details were
                  typed into a form rather than learned from traffic. Info, not
                  success: it is a neutral fact about where the record came
                  from, and nothing about it is good news. */}
              {contact.tags.map((tag) => (
                <Badge key={tag} shape="pill" tone="info">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>
      <EditContactDialog
        /** Reopened on a contact that changed under it should show what it
         *  says now, not what it said when the panel mounted. */
        key={`${contact.id}:${contact.name ?? ''}:${String(contact.isHidden)}`}
        contact={contact}
        isOpen={isEditing}
        onOpenChange={setIsEditing}
      />
    </aside>
  );
}

/** A plain, unclickable label. The default is drawn, the rest are filled — two
 *  variants the badge already has, so nothing here decides how either looks. */
function ContactBadge({
  icon: Icon,
  value,
  isDefault,
}: {
  icon: typeof Mail;
  value: string;
  isDefault: boolean;
}) {
  const marking = isDefault
    ? ({ variant: 'outline' } as const)
    : ({ tone: 'neutral' } as const);

  return (
    <Badge {...marking}>
      <Icon strokeWidth={2.5} />
      {value}
    </Badge>
  );
}

export function ContactInfoPanelSkeleton() {
  return (
    <aside className="flex h-full min-w-[384px] flex-col border-l border-border bg-background">
      <div className="border-b border-border p-5">
        <div className="h-6 w-48 rounded-md bg-muted" />
        <div className="mt-3 h-5 w-32 rounded-md bg-muted" />
      </div>
    </aside>
  );
}
